import { useEffect, useState } from 'react';

// ── Brotherhood+ signature entrance music ────────────────────────────────────
// A SINGLE persistent HTMLAudioElement that lives outside the React tree, so it
// survives every route change without ever being recreated or restarted.
// - Starts ONLY on a user gesture (the ENTER tap) — respects browser autoplay.
// - Fades in smoothly; loops the full track.
// - Preserves position / play-pause / volume / mute across navigation.
// - Ducks (pauses) automatically when any other <audio>/<video> plays, and
//   resumes when that media stops — so two audio experiences never compete.

const SRC = '/audio/brotherhood-theme.mp3';
const TARGET_VOLUME = 0.5;

let audio = null;
let fadeTimer = null;
let desiredVolume = TARGET_VOLUME; // user-chosen level; fades target this
const externalMedia = new Set(); // other media elements currently playing
const listeners = new Set();

let state = {
  hasStarted: false,
  playing: false,
  muted: false,
  volume: TARGET_VOLUME,
  desiredVolume: TARGET_VOLUME,
  currentTime: 0,
  duration: 0,
  duckedByMedia: false,
  userPaused: false,
  homeExpandedInView: true, // is the Home expanded player currently on-screen
};

function emit() {
  const snapshot = { ...state };
  listeners.forEach((l) => l(snapshot));
}
function set(patch) {
  state = { ...state, ...patch };
  emit();
}

function fadeTo(target, ms) {
  if (!audio) return;
  if (fadeTimer) clearInterval(fadeTimer);
  const startVol = audio.volume;
  const steps = Math.max(1, Math.round(ms / 40));
  let i = 0;
  fadeTimer = setInterval(() => {
    i += 1;
    const v = startVol + (target - startVol) * (i / steps);
    audio.volume = Math.max(0, Math.min(1, v));
    if (i >= steps) { clearInterval(fadeTimer); fadeTimer = null; }
  }, 40);
}

function attachMediaDucking() {
  const onExternalPlay = (e) => {
    const t = e.target;
    if (!(t instanceof HTMLMediaElement) || t === audio) return;
    externalMedia.add(t);
    if (audio && !audio.paused) {
      set({ duckedByMedia: true });
      audio.pause();
    }
  };
  const onExternalStop = (e) => {
    const t = e.target;
    if (t === audio) return;
    externalMedia.delete(t);
    if (externalMedia.size === 0 && state.duckedByMedia && !state.userPaused) {
      set({ duckedByMedia: false });
      audio.play().then(() => fadeTo(desiredVolume, 800)).catch(() => {});
    }
  };
  // Capture phase — media 'play'/'pause'/'ended' don't bubble.
  document.addEventListener('play', onExternalPlay, true);
  document.addEventListener('pause', onExternalStop, true);
  document.addEventListener('ended', onExternalStop, true);
}

function ensureAudio() {
  if (audio) return audio;
  audio = new Audio(SRC);
  audio.loop = true;
  audio.preload = 'auto';
  audio.volume = 0;
  try {
    const m = localStorage.getItem('bh_music_muted');
    if (m != null) audio.muted = m === '1';
    const v = localStorage.getItem('bh_music_volume');
    if (v != null) { const nv = parseFloat(v); if (!Number.isNaN(nv)) { desiredVolume = Math.max(0, Math.min(1, nv)); state.desiredVolume = desiredVolume; } }
  } catch { /* ignore */ }
  audio.addEventListener('timeupdate', () =>
    set({ currentTime: audio.currentTime, duration: audio.duration || 0 }));
  audio.addEventListener('play', () => set({ playing: true }));
  audio.addEventListener('pause', () => set({ playing: false }));
  audio.addEventListener('volumechange', () => set({ muted: audio.muted, volume: audio.volume }));
  attachMediaDucking();
  return audio;
}

// Called on the ENTER tap (a real user gesture → autoplay allowed).
async function start() {
  const a = ensureAudio();
  set({ hasStarted: true, userPaused: false });
  try {
    a.volume = 0;
    await a.play();
    fadeTo(desiredVolume, 1600);
  } catch { /* blocked — user can start via the control */ }
}

// Hard stop — used once the member is inside the logged-in app, so the
// signature music never plays past the entrance. Hides the mini control too.
function stop() {
  if (fadeTimer) { clearInterval(fadeTimer); fadeTimer = null; }
  if (audio) { try { audio.pause(); } catch { /* ignore */ } }
  set({ hasStarted: false, playing: false, userPaused: true });
}

function togglePlay() {
  const a = ensureAudio();
  if (a.paused) {
    set({ userPaused: false, hasStarted: true });
    a.play().then(() => fadeTo(desiredVolume, 600)).catch(() => {});
  } else {
    set({ userPaused: true });
    a.pause();
  }
}

function toggleMute() {
  const a = ensureAudio();
  a.muted = !a.muted;
  try { localStorage.setItem('bh_music_muted', a.muted ? '1' : '0'); } catch { /* ignore */ }
}

function setVolume(v) {
  const a = ensureAudio();
  const nv = Math.max(0, Math.min(1, v));
  desiredVolume = nv;
  set({ desiredVolume: nv });
  if (fadeTimer) { clearInterval(fadeTimer); fadeTimer = null; }
  a.volume = nv;
  if (nv > 0 && a.muted) a.muted = false; // moving the slider up unmutes
  try {
    localStorage.setItem('bh_music_volume', String(nv));
    localStorage.setItem('bh_music_muted', a.muted ? '1' : '0');
  } catch { /* ignore */ }
}

function seek(t) {
  const a = ensureAudio();
  const dur = a.duration || 0;
  const nt = Math.max(0, Math.min(dur || t, t));
  try { a.currentTime = nt; } catch { /* ignore */ }
  set({ currentTime: nt });
}

function setHomeExpandedInView(v) {
  if (state.homeExpandedInView !== !!v) set({ homeExpandedInView: !!v });
}

export const entranceAudio = { start, stop, togglePlay, toggleMute, setVolume, seek, setHomeExpandedInView };

export function useEntranceAudio() {
  const [s, setS] = useState(state);
  useEffect(() => {
    listeners.add(setS);
    setS(state);
    return () => { listeners.delete(setS); };
  }, []);
  return { ...s, start, togglePlay, toggleMute, setVolume, seek, setHomeExpandedInView };
}
